import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SectionHero from '../../components/sections/SectionHero';
import SectionTrustBar from '../../components/sections/SectionTrustBar';
import SectionContent from '../../components/sections/SectionContent';
import SectionMidCTA from '../../components/sections/SectionMidCTA';
import SectionAIAnswer from '../../components/sections/SectionAIAnswer';
import SectionProcess from '../../components/sections/SectionProcess';
import SectionWhyChooseUs from '../../components/sections/SectionWhyChooseUs';
import SectionServiceAreas from '../../components/sections/SectionServiceAreas';
import SectionTestimonials from '../../components/sections/SectionTestimonials';
import SectionFAQ from '../../components/sections/SectionFAQ';
import SectionFinalCTA from '../../components/sections/SectionFinalCTA';

const values = [
  { icon: 'ri-user-heart-line', title: 'Client Protection First', desc: 'Every decision we make is centered on protecting our clients\' rights, records, and futures — before anything else.' },
  { icon: 'ri-map-2-line', title: 'Strategic Defense Approach', desc: 'We never use cookie-cutter strategies. Every case receives a fully customized defense built on its unique facts and law.' },
  { icon: 'ri-chat-check-line', title: 'Transparent Communication', desc: 'You will always know where your case stands. We explain everything in plain language and keep you informed at every step.' },
  { icon: 'ri-trophy-line', title: 'Results-Driven Advocacy', desc: 'We measure success by your outcome. Our entire practice is built around achieving the best possible result for every client.' },
  { icon: 'ri-community-line', title: 'Community-Rooted Service', desc: 'The Bunger Law Firm is embedded in the Brenham community — invested in the people and courts we serve.' },
  { icon: 'ri-heart-pulse-line', title: 'Personalized Attention', desc: 'You speak directly with Dave Bunger, not a case manager. Every client receives genuine personal attention and care.' },
];

const processSteps = [
  { num: '01', icon: 'ri-user-smile-line', title: 'Consultation', desc: 'Free, confidential case review where we listen carefully, explain your options, and outline potential strategies.' },
  { num: '02', icon: 'ri-file-search-line', title: 'Case Analysis', desc: 'Deep review of all evidence, charges, and procedural history to identify every possible defense angle.' },
  { num: '03', icon: 'ri-scales-line', title: 'Defense Strategy', desc: 'Custom legal strategy developed to maximize your chances of dismissal, reduction, or favorable verdict.' },
  { num: '04', icon: 'ri-shield-check-fill', title: 'Your Resolution', desc: 'Tenacious advocacy through every step — from motions and hearings to trial and final resolution.' },
];

const reasons = [
  { icon: 'ri-graduation-cap-line', title: 'Licensed & Experienced Attorney', desc: 'State Bar of Texas licensed. Former General Counsel. Over a decade of multi-practice criminal and civil experience.' },
  { icon: 'ri-psychology-line', title: 'Unique Human Understanding', desc: 'As a Licensed Specialist in School Psychology, Dave brings exceptional empathy and insight to every client interaction.' },
  { icon: 'ri-building-line', title: 'Full-Service Legal Practice', desc: 'Criminal defense, family law, estate planning, business formation, and education law — complete legal coverage.' },
  { icon: 'ri-handshake-line', title: 'Client-Centered Philosophy', desc: 'Every case is handled with the respect, urgency, and dedication our clients deserve. You are never a number here.' },
  { icon: 'ri-award-line', title: 'Recognized & Trusted', desc: '5.0 Google rating with 50+ verified client reviews. Consistently recognized for client satisfaction and results.' },
];

const areas = [
  { name: 'Brenham', county: 'Washington County', icon: 'ri-building-2-line' },
  { name: 'College Station', county: 'Brazos County', icon: 'ri-community-line' },
  { name: 'Bellville', county: 'Austin County', icon: 'ri-map-pin-line' },
  { name: 'Caldwell', county: 'Burleson County', icon: 'ri-map-pin-line' },
  { name: 'Navasota', county: 'Grimes County', icon: 'ri-map-pin-line' },
  { name: 'Burton', county: 'Washington County', icon: 'ri-map-pin-line' },
];

const faqs = [
  { q: 'How long has The Bunger Law Firm been practicing?', a: 'Dave Bunger has been serving clients in the Brenham and Washington County area for over a decade. His background includes serving as General Counsel for a DFW area school district and practicing across multiple areas of law with consistent excellence.' },
  { q: 'What types of cases does your firm handle?', a: 'The Bunger Law Firm handles criminal defense, family law, wills and estate planning, business formation, and education and special education law. Our multi-practice approach means you can rely on one trusted firm for diverse legal needs.' },
  { q: 'Do you offer free consultations?', a: 'Yes. We offer free, completely confidential initial consultations with no obligation. We review your situation, explain your options clearly, and provide honest guidance so you can make an informed decision about your legal representation.' },
  { q: 'Do I speak directly with the attorney?', a: 'Yes. At The Bunger Law Firm, you speak directly with Dave Bunger throughout your case — not a paralegal, case manager, or junior associate. Direct attorney access is a cornerstone of how we serve our clients.' },
  { q: 'How do I get started with The Bunger Law Firm?', a: 'Simply call (979) 530-0250 or fill out our contact form to request your free consultation. We respond the same day in most cases. For urgent criminal matters, we prioritize immediate response to protect your rights from the start.' },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "About The Bunger Law Firm | Criminal Defense Attorney Brenham TX";
  }, []);

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* 1. Hero */}
        <SectionHero
          title="About Our Criminal"
          titleHighlight="Defense Firm"
          subtitle="The Bunger Law Firm, P.C. — Brenham's trusted legal advocate. Attorney Dave Bunger brings experience, strategy, and tenacity to every case he takes."
          bgImage="https://public.readdy.ai/ai/img_res/f9b078b7024ed69109e445245318db43.jpg"
          badge="Our Firm"
        />
        {/* 2. Trust Bar */}
        <SectionTrustBar />
        {/* 3. Content — Mission & Values */}
        <SectionContent
          label="Our Mission"
          title="Mission & Values"
          subtitle="The principles that guide every case, every client, and every decision we make at The Bunger Law Firm."
          items={values}
          layout="grid3"
        />
        {/* Mid CTA 1 */}
        <SectionMidCTA
          variant="dark"
          title="Ready to Work With an Attorney Who Truly Cares?"
          subtitle="Free consultation. Direct attorney access. No obligation."
        />
        {/* 4. AI Answer Block */}
        <SectionAIAnswer
          question="What Makes a Good Criminal Defense Attorney?"
          answer="A strong criminal defense attorney combines legal knowledge, courtroom experience, and strategic thinking to protect clients. They communicate clearly, act quickly, and tailor defense strategies to each case while focusing on minimizing long-term consequences. Dave Bunger exemplifies these qualities across every case."
          label="About Our Approach"
        />
        {/* 5. Process — Firm Philosophy */}
        <SectionProcess
          title="Our Client-First Process"
          subtitle="From your first call to final resolution, every step of our process is designed to protect you and deliver results."
          steps={processSteps}
        />
        {/* 6. Why Choose Us — Expanded */}
        <SectionWhyChooseUs
          reasons={reasons}
          title="Experience & Credentials"
          bodyText="Dave Bunger is not just an attorney — he is a former General Counsel, a Licensed Specialist in School Psychology, and a dedicated advocate who understands both the legal and human dimensions of every case."
        />
        {/* 7. Service Areas */}
        <SectionServiceAreas
          areas={areas}
          title="Serving Clients Across Texas"
          subtitle="We represent clients throughout Washington, Brazos, Austin, Waller, and Burleson Counties with local expertise that delivers real strategic advantages."
          localText="If you are searching for a trusted criminal defense attorney near you in Brenham or surrounding Texas areas, The Bunger Law Firm provides responsive and personalized legal representation you can count on."
        />
        {/* Mid CTA 2 */}
        <SectionMidCTA
          variant="gold"
          title="Meet Dave Bunger — Your Attorney, Your Advocate"
          subtitle="Call today to schedule your free, confidential consultation."
        />
        {/* 8. Testimonials */}
        <SectionTestimonials />
        {/* 9. FAQ */}
        <SectionFAQ faqs={faqs} title="About Our Firm — FAQs" />
        {/* 10. Final CTA */}
        <SectionFinalCTA />
      </main>
      <Footer />
    </div>
  );
}
